import { useState } from 'react';
import { faSliders } from '@fortawesome/pro-regular-svg-icons/faSliders';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Button, Divider, Menu, Radio, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { FormattedMessage, useIntl } from 'react-intl';

import { rateFilterMenuMessages } from './rate-filter-menu-messages';
import { countActiveRateFilters, DEFAULT_RATE_FILTERS, isRateServiceTypeFilter, RATE_SERVICE_TYPE_FILTER } from './rate-filtering';
import styles from './RateFilterMenu.module.css';

import type { RateFilters } from './rate-filtering';

type RateFilterMenuProps = {
  filters: RateFilters;
  onApplyFilters: (next: RateFilters) => void;
  onTriggerInteract?: () => void;
  zIndex?: number;
};

export const RateFilterMenu = ({ filters, onApplyFilters, onTriggerInteract, zIndex }: RateFilterMenuProps) => {
  const intl = useIntl();
  const [isOpen, { open, close }] = useDisclosure(false);
  const [draftFilters, setDraftFilters] = useState<RateFilters>(filters);

  const activeCount = countActiveRateFilters(filters);

  const handleOpen = () => {
    setDraftFilters(filters);
    open();
    onTriggerInteract?.();
  };

  const handleDraftServiceTypeChange = (value: string) => {
    setDraftFilters({ serviceType: isRateServiceTypeFilter(value) ? value : null });
  };

  const handleClear = () => {
    onApplyFilters(DEFAULT_RATE_FILTERS);
    close();
  };

  const handleApply = () => {
    onApplyFilters(draftFilters);
    close();
  };

  const isApplyDisabled = draftFilters.serviceType === null;

  return (
    <Menu
      closeOnItemClick={false}
      onChange={(opened) => (opened ? handleOpen() : close())}
      opened={isOpen}
      position='bottom-end'
      zIndex={zIndex}
    >
      <Menu.Target>
        <ActionIcon
          aria-label={intl.formatMessage(rateFilterMenuMessages.filterAriaLabel)}
          className={styles.filterTrigger}
          data-has-filters={activeCount > 0}
          data-testid='rate-filter-menu-target'
          variant='ghost'
        >
          <FontAwesomeIcon icon={faSliders} />
          {activeCount > 0 && (
            <>
              <span className={styles.filterCount} data-testid='rate-filter-badge'>
                {activeCount}
              </span>
              <span className={styles.filterDot} data-testid='rate-filter-dot' />
            </>
          )}
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label className={styles.filterLabel}>
          <FormattedMessage {...rateFilterMenuMessages.filterByLabel} />
        </Menu.Label>
        <div className={styles.filterSection}>
          <Text className={styles.filterSectionTitle} component='div' size='sm'>
            <FormattedMessage {...rateFilterMenuMessages.typeFilterLabel} />
          </Text>
          <Radio.Group onChange={handleDraftServiceTypeChange} value={draftFilters.serviceType ?? ''}>
            <div className={styles.filterOptions}>
              <Radio
                data-testid='rate-filter-option-direct'
                label={<FormattedMessage {...rateFilterMenuMessages.typeDirect} />}
                size='sm'
                value={RATE_SERVICE_TYPE_FILTER.Direct}
              />
              <Radio
                data-testid='rate-filter-option-indirect'
                label={<FormattedMessage {...rateFilterMenuMessages.typeIndirect} />}
                size='sm'
                value={RATE_SERVICE_TYPE_FILTER.Indirect}
              />
            </div>
          </Radio.Group>
        </div>
        <Divider className={styles.filterDivider} />
        <div className={styles.filterFooter}>
          <Button data-testid='rate-filter-clear' onClick={handleClear} size='xs' variant='tertiary'>
            <FormattedMessage {...rateFilterMenuMessages.clearFilters} />
          </Button>
          <Button data-testid='rate-filter-apply' disabled={isApplyDisabled} onClick={handleApply} size='xs' variant='primary'>
            <FormattedMessage {...rateFilterMenuMessages.applyFilters} />
          </Button>
        </div>
      </Menu.Dropdown>
    </Menu>
  );
};
